import React from 'react';
import { GlassCard } from './GlassCard';

export interface KPICardProps {
  value: string | number;
  label: string;
  delta?: string;
  deltaPositive?: boolean;
  progress?: number;
  className?: string;
  icon?: React.ReactNode;
}

export const KPICard: React.FC<KPICardProps> = ({
  value,
  label,
  delta,
  deltaPositive = true,
  progress,
  className = '',
  icon,
}) => {
  // SVG ring gauge
  const RingGauge: React.FC<{ progress: number }> = ({ progress }) => {
    const radius = 28;
    const stroke = 4;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    const containerStyles: React.CSSProperties = {
      position: 'relative',
      width: radius * 2,
      height: radius * 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    const valueStyles: React.CSSProperties = {
      position: 'absolute',
      fontSize: 'var(--text-xs)',
      fontWeight: 'var(--font-semibold)',
      color: 'var(--text)',
    };

    return (
      <div style={containerStyles}>
        <svg
          height={radius * 2}
          width={radius * 2}
          style={{ transform: 'rotate(-90deg)' }}
        >
          <circle
            stroke="var(--divider)"
            strokeWidth={stroke}
            fill="transparent"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          <circle
            stroke="var(--primary-500)"
            strokeWidth={stroke}
            strokeDasharray={circumference + ' ' + circumference}
            style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease' }}
            strokeLinecap="round"
            fill="transparent"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </svg>
        <span style={valueStyles}>{progress}%</span>
      </div>
    );
  };

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  };

  const contentStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-1)',
    flex: 1,
  };

  const valueContainerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
  };

  const valueStyles: React.CSSProperties = {
    fontSize: 'var(--text-3xl)',
    fontWeight: 'var(--font-light)',
    color: 'var(--text)',
    lineHeight: 'var(--leading-tight)',
  };

  const labelStyles: React.CSSProperties = {
    fontSize: 'var(--text-sm)',
    color: 'var(--text-secondary)',
  };

  const deltaStyles: React.CSSProperties = {
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--font-medium)',
    color: deltaPositive ? 'var(--success)' : 'var(--danger)',
    background: deltaPositive ? 'rgba(54, 211, 153, 0.15)' : 'rgba(248, 113, 113, 0.15)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-full)',
    border: `1px solid ${deltaPositive ? 'rgba(54, 211, 153, 0.25)' : 'rgba(248, 113, 113, 0.25)'}`,
  };

  const iconStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--primary-100)',
    color: 'var(--primary-500)',
    marginBottom: 'var(--space-2)',
  };

  return (
    <GlassCard variant="default" padding="md" className={className}>
      {icon && <div style={iconStyles}>{icon}</div>}
      <div style={containerStyles}>
        <div style={contentStyles}>
          <div style={valueContainerStyles}>
            <span style={valueStyles}>{value}</span>
            {delta && <span style={deltaStyles}>{delta}</span>}
          </div>
          <span style={labelStyles}>{label}</span>
        </div>
        {typeof progress === 'number' && <RingGauge progress={progress} />}
      </div>
    </GlassCard>
  );
};

export default KPICard;
